import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { MembroDaEquipe } from '../../acesso';
import { EstadoVazio, ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { linhaClicavel } from '../../design/linha-clicavel';
import { hojeBrasilia } from '../../paineis';
import {
  NOME_DA_CATEGORIA,
  curvaDoSaldo,
  filtrarEventos,
  fraseDaConta,
  lerListaDaVida,
  lerOpcoesDaVida,
  lerVidaDoProduto,
  porDia,
  unidades,
} from '../../vida-do-produto';
import type {
  CasoDaVida,
  CategoriaDaVida,
  EventoDaVida,
  FiltroDoGiro,
  FiltrosDaLista,
  ListaDaVida,
  OpcoesDaVida,
  OrdemDaLista,
  VidaDoProduto,
} from '../../vida-do-produto';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Leitor } from '../balconista/Leitor';
import {
  BarraDeFiltros,
  Carregando,
  irPara,
  lerFiltros,
  textoDoPeriodo,
} from '../paineis/pecasDaVisao';
import type { Filtro } from '../paineis/pecasDaVisao';
import '../central/central.css';
import './vida.css';

/**
 * VIDA DO PRODUTO (dono, 23/10/2026) — menu LOJA. Duas telas num endereço só:
 *   #/vida-do-produto?…filtros      a lista dos produtos que se mexeram no período
 *   #/vida-do-produto?id=…&p=…      a vida de um produto: o extrato do HOS com quem fez cada coisa
 *
 * O saldo depois de cada movimento é o do HOS (âncora: o saldo de agora). Sem HOS, a tela diz o
 * que faltou e deixa o saldo em branco — nunca um zero no lugar.
 */

const ROTA = 'vida-do-produto';
const PADRAO: Filtro = 'mes';
const FILTROS_DA_VIDA: readonly (readonly [Filtro, string])[] = [
  ['hoje', 'Hoje'],
  ['ontem', 'Ontem'],
  ['semana', 'Semana'],
  ['mes', 'Mês'],
  ['mes_passado', 'Mês passado'],
  ['trimestre', 'Trimestre'],
  ['livre', 'Período'],
];

const GIROS: readonly [FiltroDoGiro, string][] = [
  ['', 'Giro: tudo'],
  ['contado', 'Passou no Giro'],
  ['com_divergencia', 'Deu divergência'],
  ['sem_divergencia', 'Sem divergência'],
  ['igor_achou', 'Estoque: achou'],
  ['igor_parte', 'Estoque: achou parte'],
  ['igor_nao_achou', 'Estoque: não achou'],
];
const ORDENS: readonly [OrdemDaLista, string][] = [
  ['recentes', 'Mexeu por último'],
  ['mais_vendidos', 'Mais vendidos'],
  ['mais_divergencias', 'Mais divergências'],
  ['mais_contados', 'Mais contados no Giro'],
  ['mais_chegou', 'Mais chegou na loja'],
  ['descricao', 'Nome (A–Z)'],
];

const ICONE_DA_CATEGORIA: Readonly<Record<CategoriaDaVida, NomeDoIcone>> = {
  ENTRADA: 'box',
  CHEGADA: 'encomendar',
  VENDA: 'receipt',
  AJUSTE: 'edit',
  GIRO: 'shelf',
  DIVERGENCIA: 'warn',
  IGOR: 'search',
  CADASTRO: 'file',
};
const CATEGORIAS = Object.keys(NOME_DA_CATEGORIA) as CategoriaDaVida[];

const EXTRAS = [
  'busca',
  'grupo',
  'classificacao',
  'fabricante',
  'setor',
  'giro',
  'todos',
  'ordem',
  'pagina',
] as const;
type Extra = (typeof EXTRAS)[number];

const reais = (v: string | null) =>
  v === null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const hora = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
const diaPorExtenso = (dia: string, hoje: string) => {
  const d = new Date(`${dia}T12:00:00-03:00`);
  const texto = d.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  if (dia === hoje) return `Hoje, ${texto.split(', ')[1] ?? texto}`;
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};
const quandoRelativo = (iso: string | null, hoje: string) => {
  if (!iso) return '—';
  const dia = new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  if (dia === hoje) return `hoje ${hora(iso)}`;
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)} ${hora(iso)}`;
};

export function VidaDoProdutoTela({ parametros }: { parametros: URLSearchParams }) {
  const id = parametros.get('id');
  return id ? (
    <TelaDoProduto key={id + parametros.toString()} id={id} parametros={parametros} />
  ) : (
    <TelaDaLista parametros={parametros} />
  );
}

/* ================================================================== LISTA */

function TelaDaLista({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje, { padrao: PADRAO });
  const extra = (k: Extra) => parametros.get(k) ?? '';
  const extras: Record<Extra, string> = Object.fromEntries(
    EXTRAS.map((k) => [k, extra(k)]),
  ) as Record<Extra, string>;
  const filtros: FiltrosDaLista = {
    ...f.periodo,
    busca: extras.busca,
    grupo: extras.grupo,
    classificacao: extras.classificacao,
    fabricante: extras.fabricante,
    setor: extras.setor,
    pessoa: f.pessoa ?? '',
    giro: (GIROS.some(([g]) => g === extras.giro) ? extras.giro : '') as FiltroDoGiro,
    todos: extras.todos === '1',
    ordem: (ORDENS.some(([o]) => o === extras.ordem) ? extras.ordem : 'recentes') as OrdemDaLista,
    pagina: Math.max(1, Number(extras.pagina) || 1),
  };
  const chave = JSON.stringify(filtros);

  const [opcoes, setOpcoes] = useState<OpcoesDaVida | null>(null);
  const [lista, setLista] = useState<ListaDaVida | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState(filtros.busca);
  const [lendo, setLendo] = useState(false);
  useEffect(() => setBusca(filtros.busca), [filtros.busca]);

  useEffect(() => {
    void lerOpcoesDaVida().then((r) => r.ok && setOpcoes(r.dados));
  }, []);
  const carregar = useCallback(() => {
    setErro(false);
    setLista(null);
    void lerListaDaVida(filtros).then((r) => (r.ok ? setLista(r.dados) : setErro(true)));
  }, [chave]);
  useEffect(carregar, [carregar]);

  /** Troca um filtro mantendo os outros (e volta para a página 1). */
  const mudar = (mudancas: Partial<Record<Extra, string>>) =>
    irPara(f, ROTA, { ...extras, pagina: '', ...mudancas }, PADRAO);

  const equipe: MembroDaEquipe[] = (opcoes?.pessoas ?? []).map((p) => ({
    personId: p.id,
    accountId: p.id,
    pedidoId: null,
    nome: p.nome,
    login: null,
    perfis: [],
    situacao: 'ativo',
    suspensoAte: null,
    ultimoAcesso: null,
  }));
  const paginas = lista ? Math.max(1, Math.ceil(lista.total / lista.tamanhoDaPagina)) : 1;
  const abrir = (pid: string) => {
    const q = new URLSearchParams(parametros);
    q.delete('pagina');
    q.set('id', pid);
    window.location.hash = `#/${ROTA}?${q.toString()}`;
  };

  return (
    <section className="cv vp">
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <div className="vg-saudacao">Loja</div>
            <h1>Vida do produto</h1>
            <div className="vg-contexto">
              Tudo o que aconteceu com cada produto: entrada, venda, ajuste, Giro e conferência do
              estoque · {textoDoPeriodo(f.filtro, f.periodo, hoje)}
            </div>
          </div>
        </header>

        <form
          className="vp-busca"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            mudar({ busca: busca.trim() });
          }}
        >
          <Icone nome="search" tamanho={18} traco={1.7} />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome, código, EAN ou laboratório"
            aria-label="Buscar produto"
          />
          <button
            type="button"
            className="vp-busca-leitor"
            onClick={() => setLendo(true)}
            title="Ler o código pela câmera"
          >
            <Icone nome="scan" tamanho={17} traco={1.7} />
            <span>Ler código</span>
          </button>
          <button type="submit" className="vp-busca-ir">
            Buscar
          </button>
        </form>
        {lendo && (
          <Leitor
            aoLer={(codigo) => {
              setLendo(false);
              setBusca(codigo);
              mudar({ busca: codigo });
            }}
            aoFechar={() => setLendo(false)}
          />
        )}

        <BarraDeFiltros
          f={f}
          hoje={hoje}
          rota={ROTA}
          extras={{ ...extras, pagina: '' }}
          filtros={FILTROS_DA_VIDA}
          padrao={PADRAO}
          equipe={opcoes ? equipe : undefined}
        >
          <Seletor
            rotulo="Setor do Giro"
            valor={filtros.setor}
            vazio="Todos os setores"
            opcoes={(opcoes?.setores ?? []).map((s) => [s.id, s.nome])}
            aoMudar={(v) => mudar({ setor: v })}
          />
          <Seletor
            rotulo="Desfecho do Giro"
            valor={filtros.giro}
            opcoes={GIROS}
            aoMudar={(v) => mudar({ giro: v })}
          />
        </BarraDeFiltros>

        <div className="vp-filtros-2" role="toolbar" aria-label="Filtros do cadastro">
          <Seletor
            rotulo="Grupo"
            valor={filtros.grupo}
            vazio="Todos os grupos"
            opcoes={(opcoes?.grupos ?? []).map((g) => [g, g])}
            aoMudar={(v) => mudar({ grupo: v })}
          />
          <Seletor
            rotulo="Classificação"
            valor={filtros.classificacao}
            vazio="Todas as classificações"
            opcoes={(opcoes?.classificacoes ?? []).map((g) => [g, g])}
            aoMudar={(v) => mudar({ classificacao: v })}
          />
          <Seletor
            rotulo="Laboratório"
            valor={filtros.fabricante}
            vazio="Todos os laboratórios"
            opcoes={(opcoes?.fabricantes ?? []).map((g) => [g, g])}
            aoMudar={(v) => mudar({ fabricante: v })}
          />
          <Seletor
            rotulo="Ordem"
            valor={filtros.ordem}
            opcoes={ORDENS}
            aoMudar={(v) => mudar({ ordem: v === 'recentes' ? '' : v })}
          />
          <label className="vp-check">
            <input
              type="checkbox"
              checked={filtros.todos}
              onChange={(e) => mudar({ todos: e.target.checked ? '1' : '' })}
            />
            Mostrar também quem não se mexeu
          </label>
        </div>

        {erro ? (
          <ErroDeArea oQue="a lista de produtos" aoTentar={carregar} />
        ) : !lista ? (
          <Carregando />
        ) : lista.itens.length === 0 ? (
          <EstadoVazio
            icone="box"
            titulo="Nenhum produto com esses filtros"
            texto={
              filtros.todos
                ? 'Mude a busca ou limpe os filtros.'
                : 'Nenhum produto se mexeu no período com esses filtros. Aumente o período ou marque "Mostrar também quem não se mexeu".'
            }
          />
        ) : (
          <>
            <div className="vp-contagem">
              {lista.total.toLocaleString('pt-BR')} produto{lista.total === 1 ? '' : 's'}
              {filtros.todos ? '' : ' que se mexeram no período'}
            </div>
            <div className="vp-tabela" aria-label="Produtos">
              <div className="vp-tr vp-th" aria-hidden="true">
                <span>Produto</span>
                <span className="vp-num">Estoque</span>
                <span className="vp-num">Vendeu</span>
                <span className="vp-num">Chegou</span>
                <span className="vp-num">Giro</span>
                <span>Divergência e estoque</span>
                <span className="vp-num">Último fato</span>
              </div>
              {lista.itens.map((p) => (
                <div
                  key={p.id}
                  className="vp-tr"
                  aria-label={`Abrir a vida de ${p.descricao}`}
                  {...linhaClicavel(() => abrir(p.id))}
                >
                  <span className="vp-produto">
                    <FotoDoProduto url={p.foto} nome={p.descricao} tamanho={40} />
                    <span>
                      <b>{p.descricao}</b>
                      <small>
                        {[p.fabricante, p.grupo, p.codigoHos ? `cód. ${p.codigoHos}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </small>
                    </span>
                  </span>
                  <span className="vp-num" data-rotulo="Estoque">
                    {p.estoque === null ? '—' : unidades(p.estoque)}
                  </span>
                  <span className="vp-num" data-rotulo="Vendeu">
                    {p.vendido ? (
                      <>
                        {unidades(p.vendido)}
                        <small>
                          {p.cupons} cupo{p.cupons === 1 ? 'm' : 'ns'}
                        </small>
                      </>
                    ) : (
                      <i>—</i>
                    )}
                  </span>
                  <span className="vp-num" data-rotulo="Chegou">
                    {p.chegou ? unidades(p.chegou) : <i>—</i>}
                  </span>
                  <span className="vp-num" data-rotulo="Giro">
                    {p.contagens ? (
                      <>
                        {p.contagens}×<small>repôs {unidades(p.reposto)}</small>
                      </>
                    ) : (
                      <i>—</i>
                    )}
                  </span>
                  <span className="vp-selos" data-rotulo="Divergência">
                    {p.divergencias > 0 && (
                      <em className="vp-selo vp-alerta">
                        {p.divergencias} divergência{p.divergencias === 1 ? '' : 's'}
                      </em>
                    )}
                    {p.igorAchou > 0 && <em className="vp-selo vp-ok">achou {p.igorAchou}</em>}
                    {p.igorParte > 0 && <em className="vp-selo vp-alerta">parte {p.igorParte}</em>}
                    {p.igorNaoAchou > 0 && (
                      <em className="vp-selo vp-erro">não achou {p.igorNaoAchou}</em>
                    )}
                    {p.contagens > 0 && p.divergencias === 0 && (
                      <em className="vp-selo vp-ok">sem divergência</em>
                    )}
                  </span>
                  <span className="vp-num vp-quando" data-rotulo="Último fato">
                    {quandoRelativo(p.ultimoFato, hoje)}
                  </span>
                </div>
              ))}
            </div>
            {paginas > 1 && (
              <nav className="vp-paginas" aria-label="Páginas">
                <button
                  type="button"
                  className="vg-botao"
                  disabled={filtros.pagina <= 1}
                  onClick={() => mudar({ pagina: String(filtros.pagina - 1) })}
                >
                  Anterior
                </button>
                <span>
                  Página {filtros.pagina} de {paginas}
                </span>
                <button
                  type="button"
                  className="vg-botao"
                  disabled={filtros.pagina >= paginas}
                  onClick={() => mudar({ pagina: String(filtros.pagina + 1) })}
                >
                  Próxima
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Seletor({
  rotulo,
  valor,
  vazio,
  opcoes,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  vazio?: string;
  opcoes: readonly (readonly [string, string])[];
  aoMudar: (v: string) => void;
}) {
  const padrao = vazio !== undefined ? '' : (opcoes[0]?.[0] ?? '');
  return (
    <label className={`vg-pessoa vp-seletor${valor !== padrao ? ' vp-seletor-ativo' : ''}`}>
      <select value={valor} aria-label={rotulo} onChange={(e) => aoMudar(e.target.value)}>
        {vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map(([v, t]) => (
          <option key={v} value={v}>
            {t}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ================================================================== UM PRODUTO */

function TelaDoProduto({ id, parametros }: { id: string; parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje, { padrao: PADRAO });
  const [vida, setVida] = useState<VidaDoProduto | null>(null);
  const [erro, setErro] = useState<'nao-achou' | 'falhou' | null>(null);
  const [categorias, setCategorias] = useState<ReadonlySet<CategoriaDaVida>>(new Set());
  const [pessoa, setPessoa] = useState('');
  const [texto, setTexto] = useState('');
  const [destaque, setDestaque] = useState<string | null>(null);
  const [antigoPrimeiro, setAntigoPrimeiro] = useState(false);

  const carregar = useCallback(() => {
    setErro(null);
    setVida(null);
    void lerVidaDoProduto(id, f.periodo.de, f.periodo.ate).then((r) => {
      if (r.ok) setVida(r.dados);
      else setErro('falhou');
    });
  }, [id, f.periodo.de, f.periodo.ate]);
  useEffect(carregar, [carregar]);

  const extrasDoProduto = { id };
  const voltar = () => {
    const q = new URLSearchParams(parametros);
    q.delete('id');
    const s = q.toString();
    window.location.hash = s ? `#/${ROTA}?${s}` : `#/${ROTA}`;
  };

  const eventos = useMemo(() => {
    if (!vida) return [];
    const lista = filtrarEventos(vida.eventos, { categorias, pessoa, texto });
    return antigoPrimeiro ? lista.reverse() : lista;
  }, [vida, categorias, pessoa, texto, antigoPrimeiro]);
  const pessoas = useMemo(
    () =>
      vida
        ? [...new Set(vida.eventos.flatMap((e) => (e.quem ? [e.quem.nome] : [])))].sort((a, b) =>
            a.localeCompare(b, 'pt-BR'),
          )
        : [],
    [vida],
  );
  const contagem = useMemo(() => {
    const c = new Map<CategoriaDaVida, number>();
    for (const e of vida?.eventos ?? []) c.set(e.categoria, (c.get(e.categoria) ?? 0) + 1);
    return c;
  }, [vida]);
  const alternar = (c: CategoriaDaVida) =>
    setCategorias((atual) => {
      const n = new Set(atual);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    });

  if (erro === 'nao-achou')
    return (
      <section className="cv vp">
        <div className="cv-corpo">
          <EstadoVazio
            icone="box"
            titulo="Produto não encontrado"
            texto="Volte para a lista e escolha outro."
          >
            <button type="button" className="vg-botao" onClick={voltar}>
              Voltar para a lista
            </button>
          </EstadoVazio>
        </div>
      </section>
    );

  const p = vida?.produto;
  const r = vida?.resumo;
  const maxSaldo = Math.max(
    1,
    ...(vida?.eventos ?? []).map((e) => e.saldo ?? 0),
    vida?.saldoAgora?.saldo ?? 0,
  );

  return (
    <section className="cv vp">
      <div className="cv-corpo vg">
        <button type="button" className="vp-voltar" onClick={voltar}>
          <Icone nome="arrow-l" tamanho={15} traco={1.8} />
          Todos os produtos
        </button>

        {erro ? (
          <ErroDeArea oQue="a vida do produto" aoTentar={carregar} />
        ) : !vida || !p || !r ? (
          <Carregando />
        ) : (
          <>
            <header className="vp-ficha">
              <FotoDoProduto url={p.foto} nome={p.descricao} tamanho={104} />
              <div className="vp-ficha-texto">
                <h1>{p.descricao}</h1>
                <div className="vp-ficha-linha">
                  {[p.fabricante, p.principioAtivo, p.grupo, p.classificacao]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <dl className="vp-ficha-dados">
                  {p.codigoHos && (
                    <div>
                      <dt>Código HOS</dt>
                      <dd className="vp-mono">{p.codigoHos}</dd>
                    </div>
                  )}
                  {p.ean && (
                    <div>
                      <dt>EAN</dt>
                      <dd className="vp-mono">{p.ean}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Preço</dt>
                    <dd>{reais(p.preco)}</dd>
                  </div>
                  {p.setores.length > 0 && (
                    <div>
                      <dt>Setor do Giro</dt>
                      <dd>{p.setores.join(', ')}</dd>
                    </div>
                  )}
                  {p.localizacao && (
                    <div>
                      <dt>Local</dt>
                      <dd>{p.localizacao}</dd>
                    </div>
                  )}
                </dl>
              </div>
              <div className="vp-agora" aria-live="polite">
                <span>No HOS agora</span>
                <strong>{vida.saldoAgora ? unidades(vida.saldoAgora.saldo) : '—'}</strong>
                <small>
                  {vida.saldoAgora
                    ? `lido às ${hora(vida.saldoAgora.em)}`
                    : p.estoqueCadastro !== null
                      ? `cadastro diz ${unidades(p.estoqueCadastro)}`
                      : 'sem leitura'}
                </small>
                {vida.emConferencia.length > 0 && (
                  <em>
                    + {unidades(vida.emConferencia.reduce((t, n) => t + n.unidades, 0))} em nota em
                    conferência
                  </em>
                )}
              </div>
            </header>

            <BarraDeFiltros
              f={f}
              hoje={hoje}
              rota={ROTA}
              extras={extrasDoProduto}
              filtros={FILTROS_DA_VIDA}
              padrao={PADRAO}
            />

            {vida.avisos.length > 0 && (
              <ul className="vp-avisos">
                {vida.avisos.map((a) => (
                  <li key={a}>
                    <Icone nome="warn" tamanho={15} traco={1.7} />
                    {a}
                  </li>
                ))}
              </ul>
            )}

            <Curva vida={vida} destaque={destaque} aoApontar={setDestaque} />

            <div className="vp-placar">
              <Numero rotulo="Entrou no HOS" valor={r.entrou} sinal />
              <Numero rotulo="Chegou na loja" valor={r.chegouNaLoja} />
              <Numero rotulo="Vendeu" valor={-r.vendeu} sinal>
                {r.vendedores.slice(0, 3).map((v) => (
                  <small key={v.nome}>
                    {v.nome.split(' ')[0]} {unidades(v.qtd)}
                  </small>
                ))}
              </Numero>
              <Numero
                rotulo="Ajustes no HOS"
                valor={r.ajustes}
                sinal
                tom={r.ajustes ? 'alerta' : undefined}
              />
              <Numero rotulo="Perdas" valor={-r.perdas} sinal tom={r.perdas ? 'erro' : undefined} />
              <Numero rotulo="Contagens do Giro" valor={r.contagens} sufixo="×">
                <small>repôs {unidades(r.reposto)}</small>
              </Numero>
              <Numero
                rotulo="Divergências"
                valor={r.divergencias}
                sufixo=""
                tom={r.divergencias ? 'alerta' : undefined}
              />
              <div className="vp-numero vp-numero-estoque">
                <span>Conferência do estoque</span>
                <div className="vp-igor">
                  <b className="vp-ok">{r.igor.tudo}</b> achou
                  <b className="vp-alerta">{r.igor.parte}</b> parte
                  <b className="vp-erro">{r.igor.nada}</b> não achou
                </div>
              </div>
            </div>

            {vida.casos.length > 0 && (
              <section className="vp-casos" aria-label="Divergências deste produto">
                <h2>
                  Divergências deste produto
                  <span>do começo ao fim, com o que o Iggor achou</span>
                </h2>
                {vida.casos.map((c) => (
                  <Caso key={c.numero} c={c} />
                ))}
              </section>
            )}

            <div className="vp-ferramentas">
              <div className="vp-chips" role="group" aria-label="Tipos de fato">
                {CATEGORIAS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`vp-chip vp-cat-${c.toLowerCase()}${categorias.has(c) ? ' vp-chip-on' : ''}`}
                    aria-pressed={categorias.has(c)}
                    disabled={!contagem.get(c)}
                    onClick={() => alternar(c)}
                  >
                    <Icone nome={ICONE_DA_CATEGORIA[c]} tamanho={14} traco={1.7} />
                    {NOME_DA_CATEGORIA[c]}
                    <i>{contagem.get(c) ?? 0}</i>
                  </button>
                ))}
              </div>
              <div className="vp-ferramentas-2">
                <label className="vg-pessoa vp-seletor">
                  <Icone nome="users" tamanho={14} traco={1.6} />
                  <select
                    value={pessoa}
                    aria-label="Pessoa"
                    onChange={(e) => setPessoa(e.target.value)}
                  >
                    <option value="">Todas as pessoas</option>
                    {pessoas.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="vp-procurar">
                  <Icone nome="search" tamanho={14} traco={1.7} />
                  <input
                    type="search"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder="Nota, cupom, divergência…"
                    aria-label="Procurar na linha do tempo"
                  />
                </label>
                <button
                  type="button"
                  className="vg-botao vp-ordem"
                  aria-pressed={antigoPrimeiro}
                  onClick={() => setAntigoPrimeiro((v) => !v)}
                  title="Inverte a ordem da linha do tempo"
                >
                  <Icone nome="clock" tamanho={14} traco={1.7} />
                  {antigoPrimeiro ? 'Mais antigo primeiro' : 'Mais novo primeiro'}
                </button>
                {(categorias.size > 0 || pessoa || texto) && (
                  <button
                    type="button"
                    className="vg-limpar"
                    onClick={() => {
                      setCategorias(new Set());
                      setPessoa('');
                      setTexto('');
                    }}
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>

            {eventos.length === 0 ? (
              <EstadoVazio
                icone="clock"
                titulo={
                  vida.eventos.length ? 'Nada com esses filtros' : 'Nada aconteceu no período'
                }
                texto={
                  vida.eventos.length
                    ? 'Limpe os filtros da linha do tempo.'
                    : 'Aumente o período para ver a vida do produto.'
                }
              />
            ) : (
              <ol className="vp-tempo">
                {porDia(eventos).map((g) => (
                  <li key={g.dia} className="vp-dia">
                    <h2>
                      {diaPorExtenso(g.dia, hoje)}
                      <span>
                        {g.eventos.length} fato{g.eventos.length === 1 ? '' : 's'}
                      </span>
                    </h2>
                    <ol>
                      {g.eventos.map((e) => (
                        <Fato
                          key={e.chave}
                          e={e}
                          maxSaldo={maxSaldo}
                          destacado={destaque === e.chave}
                          aoApontar={setDestaque}
                        />
                      ))}
                    </ol>
                  </li>
                ))}
              </ol>
            )}
            <p className="vp-rodape">
              {vida.fonteDoEstoque === 'HOS_RAZAO'
                ? 'Entradas, vendas, ajustes e perdas vêm do razão do HOS. "HOS" à direita de cada fato é o saldo do HOS naquela hora, contado a partir do saldo de agora. No Giro o balconista conta só a prateleira; os números de estoque do cartão são do HOS. Reposição é troca de lugar (estoque → prateleira) e não muda o saldo.'
                : 'Sem o razão do HOS: as vendas vêm do que o InovaPay já leu.'}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function Numero({
  rotulo,
  valor,
  sinal = false,
  sufixo,
  tom,
  children,
}: {
  rotulo: string;
  valor: number;
  sinal?: boolean;
  /** Contagem (não unidade): o número com este sufixo, sem "un". */
  sufixo?: string;
  tom?: 'alerta' | 'erro' | undefined;
  children?: ReactNode;
}) {
  return (
    <div className={`vp-numero${valor === 0 ? ' vp-numero-zero' : ''}${tom ? ` vp-${tom}` : ''}`}>
      <span>{rotulo}</span>
      <strong>
        {sufixo !== undefined ? `${String(valor)}${sufixo}` : unidades(valor, sinal)}
        {sufixo === undefined && <i> un</i>}
      </strong>
      {children && <div className="vp-numero-sub">{children}</div>}
    </div>
  );
}

function Fato({
  e,
  maxSaldo,
  destacado,
  aoApontar,
}: {
  e: EventoDaVida;
  maxSaldo: number;
  destacado: boolean;
  aoApontar: (chave: string | null) => void;
}) {
  return (
    <li
      className={`vp-fato vp-cat-${e.categoria.toLowerCase()}${e.cancelado ? ' vp-cancelado' : ''}${destacado ? ' vp-destacado' : ''}`}
      onMouseEnter={() => aoApontar(e.chave)}
      onMouseLeave={() => aoApontar(null)}
    >
      <time className="vp-hora" dateTime={e.em ?? e.dia}>
        {e.em ? hora(e.em) : 'sem hora'}
      </time>
      <span className="vp-marca" aria-hidden="true">
        <Icone nome={ICONE_DA_CATEGORIA[e.categoria]} tamanho={14} traco={1.8} />
      </span>
      <div className="vp-fato-corpo">
        <div className="vp-fato-titulo">
          <b>{e.titulo}</b>
          {e.selo && <em className={`vp-selo vp-${e.selo.tom}`}>{e.selo.texto}</em>}
        </div>
        {(e.quem || e.detalhe) && (
          <div className="vp-fato-linha">
            {e.quem && (
              <span className="vp-quem">
                <b>{e.quem.nome}</b> {e.quem.papel}
              </span>
            )}
            {e.detalhe && <span>{e.detalhe}</span>}
          </div>
        )}
        {e.numeros.length > 0 && (
          <dl className="vp-fato-numeros">
            {e.numeros.map((n) => (
              <div key={n.rotulo} className={n.tom ? `vp-${n.tom}` : undefined}>
                <dt>{n.rotulo}</dt>
                <dd>{n.valor}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      <div className="vp-extrato">
        {e.qtd !== null && (
          <span
            className={`vp-delta${e.qtd > 0 ? ' vp-mais' : e.qtd < 0 ? ' vp-menos' : ''}`}
            title={e.qtd > 0 ? 'Entrou no HOS' : 'Saiu do HOS'}
          >
            {unidades(e.qtd, true)} un
          </span>
        )}
        {e.saldo !== null && (
          <span
            className="vp-saldo"
            title={
              e.saldoAproximado
                ? 'Aproximado: o HOS não deu a hora de um movimento deste dia'
                : 'Saldo do HOS'
            }
          >
            <span className="vp-saldo-barra" aria-hidden="true">
              <span
                style={{
                  width: `${String(Math.max(0, Math.min(100, (e.saldo / maxSaldo) * 100)))}%`,
                }}
              />
            </span>
            <span className="vp-saldo-rotulo">HOS</span>
            {e.saldoAproximado ? '≈' : ''}
            {unidades(e.saldo)} un
          </span>
        )}
      </div>
    </li>
  );
}

const diaEHora = (iso: string) => {
  const dia = new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  });
  return `${dia} ${hora(iso)}`;
};

/** Uma divergência num bloco só: a conta (HOS × o que o Iggor achou) e os passos em ordem. */
function Caso({ c }: { c: CasoDaVida }) {
  const conta = c.conta ? fraseDaConta(c.conta) : null;
  return (
    <article className="vp-caso">
      <header className="vp-caso-topo">
        <b className="vp-mono">{c.documento}</b>
        <span className="vp-caso-titulo">{c.titulo}</span>
        <em className={`vp-selo vp-${c.selo.tom}`}>{c.selo.texto}</em>
        {c.etapa && <em className="vp-selo">{c.etapa}</em>}
        <small>
          aberta {diaEHora(c.abertaEm)}
          {c.balconista ? ` · ${c.balconista}` : ''}
        </small>
      </header>
      {conta && (
        <p className={`vp-caso-conta vp-${conta.tom}`}>
          {conta.texto}
          {c.conta && c.conta.hos !== null && (
            <small> · HOS de {diaEHora(c.conta.hosEm)}, quando o Iggor conferiu a prateleira</small>
          )}
        </p>
      )}
      {c.aviso && (
        <p className="vp-caso-aviso">
          <Icone nome="warn" tamanho={14} traco={1.7} />
          {c.aviso}
        </p>
      )}
      <ol className="vp-caso-passos">
        {c.passos.map((p) => (
          <li key={`${p.em}:${p.texto}`} className={`vp-${p.tom}`}>
            <time dateTime={p.em}>{diaEHora(p.em)}</time>
            <span>
              {p.texto}
              {p.quem && <b> · {p.quem}</b>}
            </span>
          </li>
        ))}
      </ol>
    </article>
  );
}

/** A curva do saldo no período, com cada movimento marcado. Passar o mouse liga com o fato. */
function Curva({
  vida,
  destaque,
  aoApontar,
}: {
  vida: VidaDoProduto;
  destaque: string | null;
  aoApontar: (chave: string | null) => void;
}) {
  const pontos = curvaDoSaldo(vida.eventos, vida.resumo);
  if (pontos.length < 2) return null;
  const L = 1000;
  const A = 120;
  const pad = 8;
  const primeiro = pontos[0];
  const ultimo = pontos[pontos.length - 1];
  if (!primeiro || !ultimo) return null;
  const t0 = primeiro.quando;
  const t1 = ultimo.quando;
  const max = Math.max(1, ...pontos.map((p) => p.saldo));
  const x = (t: number) => pad + ((t - t0) / Math.max(1, t1 - t0)) * (L - pad * 2);
  const y = (s: number) => A - pad - (Math.max(0, s) / max) * (A - pad * 2);
  // degraus: o saldo fica parado até o próximo movimento
  let d = `M ${String(x(t0))} ${String(y(primeiro.saldo))}`;
  for (const p of pontos.slice(1)) d += ` H ${String(x(p.quando))} V ${String(y(p.saldo))}`;
  const area = `${d} V ${String(A - pad)} H ${String(x(t0))} Z`;
  const ponto = destaque ? pontos.find((p) => p.chave === destaque) : null;
  return (
    <figure className="vp-curva">
      <figcaption>
        <span>Saldo do HOS no período</span>
        <span>
          {vida.resumo.saldoInicio !== null && (
            <>
              começou com <b>{unidades(vida.resumo.saldoInicio)}</b> ·{' '}
            </>
          )}
          {vida.resumo.saldoFim !== null && (
            <>
              terminou com <b>{unidades(vida.resumo.saldoFim)}</b>
            </>
          )}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${String(L)} ${String(A)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Curva do saldo do HOS no período"
      >
        <path d={area} className="vp-curva-area" />
        <path d={d} className="vp-curva-linha" vectorEffect="non-scaling-stroke" />
        {pontos.slice(1).map((p) => (
          <rect
            key={p.chave}
            x={x(p.quando) - 6}
            y={0}
            width={12}
            height={A}
            className="vp-curva-alvo"
            onMouseEnter={() => aoApontar(p.chave)}
            onMouseLeave={() => aoApontar(null)}
          />
        ))}
        {ponto && (
          <line
            x1={x(ponto.quando)}
            x2={x(ponto.quando)}
            y1={0}
            y2={A}
            className="vp-curva-guia"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <div className="vp-curva-eixo">
        <span>
          {new Date(t0).toLocaleDateString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
          })}
        </span>
        <span>máx. {unidades(max)}</span>
        <span>
          {new Date(t1).toLocaleDateString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
          })}
        </span>
      </div>
    </figure>
  );
}
